/**
 * Onboarding flag helpers (all client-side, no DB).
 *
 * These small booleans gate the first-run UX surfaces (welcome screen,
 * first-recipe headline, post-save overlay, habit-seeding card). We keep
 * them in localStorage so they persist across sessions per device — that's
 * the right grain for "have you seen this before".
 *
 * Anything that should reset on sign-out lives elsewhere (e.g. session-
 * scoped state in MyRecipes); these flags intentionally do NOT reset, so
 * the same person on the same device doesn't re-experience the intro.
 */

export const ONBOARDING_KEYS = {
  /** User has dismissed / progressed past the welcome screen. */
  onboarded: "cp_onboarded",
  /** User has cleaned at least one recipe successfully. */
  firstClean: "cp_first_clean",
  /** User has saved at least one recipe successfully. */
  firstSave: "cp_first_save",
  /** User has dismissed the menu-builder habit-seed card on /my-recipes. */
  menuPromptDismissed: "cp_menu_prompt_dismissed",
} as const;

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage disabled — onboarding flags become per-session, that's fine */
  }
};

export const hasFlag = (key: keyof typeof ONBOARDING_KEYS) =>
  safeGet(ONBOARDING_KEYS[key]) === "1";

export const setFlag = (key: keyof typeof ONBOARDING_KEYS) => {
  safeSet(ONBOARDING_KEYS[key], "1");
};
